import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

// Lightweight hero carousel reused across certification pages
const CertificationHeroCarousel = ({ slides, autoPlayInterval = 6000 }) => {
  const validSlides = useMemo(() => (Array.isArray(slides) ? slides.filter(slide => slide?.image) : []), [slides]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying || validSlides.length <= 1) return undefined;

    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % validSlides.length);
    }, autoPlayInterval);

    return () => clearInterval(timer);
  }, [autoPlayInterval, isAutoPlaying, validSlides.length]);

  const jumpToSlide = delta => {
    if (validSlides.length <= 1) return;
    setIsAutoPlaying(false);
    setCurrentSlide(prev => (prev + delta + validSlides.length) % validSlides.length);
  };

  if (!validSlides.length) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: 240, md: 300 },
        borderRadius: { xs: 0 },
        overflow: 'hidden',
        mb: { xs: 2, md: 3 },
        bgcolor: '#0a0a0a',
      }}>
      {validSlides.map((slide, index) => (
        <Box
          key={slide.image || index}
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${slide.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: currentSlide === index ? 1 : 0,
            zIndex: currentSlide === index ? 1 : 0,
            pointerEvents: currentSlide === index ? 'auto' : 'none',
            transition: 'opacity 0.9s ease',
          }}>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.75) 100%)',
              pointerEvents: 'none',
            }}
          />
          <Stack
            spacing={1.5}
            sx={{
              position: 'relative',
              zIndex: 1,
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              color: '#fff',
              px: { xs: 3, md: 6 },
              pointerEvents: 'none',
            }}>
            {slide.eyebrow ? (
              <Typography
                variant="overline"
                sx={theme => ({
                  color: theme.palette.primary.main,
                  fontWeight: 800,
                  letterSpacing: 3,
                  textTransform: 'uppercase',
                  fontSize: { xs: 11, md: 12 },
                })}>
                {slide.eyebrow}
              </Typography>
            ) : null}
            {slide.title ? (
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 900,
                  textShadow: '2px 4px 12px rgba(0,0,0,0.45)',
                  fontSize: { xs: 32, sm: 40, md: 52, lg: 64 },
                  lineHeight: 1.05,
                }}>
                {slide.title}
              </Typography>
            ) : null}
            {slide.subtitle ? (
              <Typography
                variant="body1"
                sx={{
                  maxWidth: 720,
                  mx: 'auto',
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: 15, md: 18 },
                  lineHeight: 1.6,
                  fontWeight: 400,
                }}>
                {slide.subtitle}
              </Typography>
            ) : null}
          </Stack>
        </Box>
      ))}

      {validSlides.length > 1 ? (
        <>
          <IconButton
            onClick={() => jumpToSlide(-1)}
            sx={theme => ({
              position: 'absolute',
              left: { xs: 8, md: 16 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': { bgcolor: theme.palette.primary.light },
            })}>
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={() => jumpToSlide(1)}
            sx={theme => ({
              position: 'absolute',
              right: { xs: 8, md: 16 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': { bgcolor: theme.palette.primary.light },
            })}>
            <ChevronRight />
          </IconButton>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              position: 'absolute',
              bottom: 18,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 3,
            }}>
            {validSlides.map((slide, index) => (
              <Box
                key={slide.image || index}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setIsAutoPlaying(false);
                  setCurrentSlide(index);
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setIsAutoPlaying(false);
                    setCurrentSlide(index);
                  }
                }}
                sx={theme => ({
                  width: currentSlide === index ? 28 : 12,
                  height: 12,
                  borderRadius: 6,
                  bgcolor: currentSlide === index ? theme.palette.primary.main : 'rgba(255,255,255,0.55)',
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    bgcolor: currentSlide === index ? theme.palette.primary.main : 'rgba(255,255,255,0.8)',
                  },
                })}
              />
            ))}
          </Stack>
        </>
      ) : null}
    </Box>
  );
};

CertificationHeroCarousel.propTypes = {
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string,
      eyebrow: PropTypes.node,
      title: PropTypes.node,
      subtitle: PropTypes.node,
    }),
  ),
  autoPlayInterval: PropTypes.number,
};

export default CertificationHeroCarousel;
